import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';

/** Strip .pdf extension and return the base name. */
function baseName(filename) {
  return filename.replace(/\.pdf$/i, '');
}

/** Create a new PDF from a subset of pages taken from srcDoc. */
async function createPdfFromPages(srcDoc, pageIndices) {
  const newDoc = await PDFDocument.create();
  const copied = await newDoc.copyPages(srcDoc, pageIndices);
  copied.forEach((p) => newDoc.addPage(p));
  return newDoc.save();
}

/** Trigger a browser file download for a Uint8Array as application/pdf. */
function downloadPdf(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  triggerDownload(blob, filename);
}

/** Trigger a browser file download for a Blob. */
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

/**
 * Derive the output PDF filename for a single group entry in a ZIP.
 *
 * @param {string} base        - File base name (no extension)
 * @param {number} groupIndex  - 0-based index of the group
 * @param {number[]} group     - 0-based page indices in this group
 * @param {'page'|'part'|'range'} namingMode
 */
function groupFileName(base, groupIndex, group, namingMode) {
  if (namingMode === 'page') {
    return `${base}-page-${group[0] + 1}.pdf`;
  }
  if (namingMode === 'range') {
    const first = group[0] + 1;
    const last = group[group.length - 1] + 1;
    return first === last
      ? `${base}-page-${first}.pdf`
      : `${base}-pages-${first}-${last}.pdf`;
  }
  // 'part' (default)
  return `${base}-part-${groupIndex + 1}.pdf`;
}

/**
 * Split a PDF into multiple files and trigger a download.
 *
 * - Single group  → direct PDF download
 * - Multiple groups → ZIP archive download
 *
 * @param {ArrayBuffer} arrayBuffer  - Source PDF bytes
 * @param {number[][]}  groups       - Array of 0-based page-index arrays
 * @param {string}      fileName     - Original file name (used as base for output names)
 * @param {'page'|'part'|'range'|'selected'} namingMode
 * @param {{ onProgress?: (done: number, total: number) => void }} [options]
 */
export async function splitAndExport(arrayBuffer, groups, fileName, namingMode = 'part', options = {}) {
  if (!groups || groups.length === 0) throw new Error('No groups to export.');

  const srcDoc = await PDFDocument.load(arrayBuffer);
  const base = baseName(fileName);
  const { onProgress } = options;

  // ── Single output ─────────────────────────────────────────────────────────
  if (groups.length === 1) {
    const bytes = await createPdfFromPages(srcDoc, groups[0]);
    const suffix = namingMode === 'selected' ? 'selected-pages' : 'split';
    downloadPdf(bytes, `${base}-${suffix}.pdf`);
    return;
  }

  // ── Multiple outputs → ZIP ────────────────────────────────────────────────
  const zip = new JSZip();

  for (let i = 0; i < groups.length; i++) {
    const bytes = await createPdfFromPages(srcDoc, groups[i]);
    const name = groupFileName(base, i, groups[i], namingMode);
    zip.file(name, bytes);
    if (onProgress) onProgress(i + 1, groups.length);
  }

  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 5 },
  });

  triggerDownload(blob, `${base}-split.zip`);
}

/**
 * Return a human-readable list of output file names for preview purposes.
 * Caps at `maxDisplay` entries then adds a "+ N more" line.
 *
 * @param {string}   base        - File base name
 * @param {number[][]} groups    - Page index groups
 * @param {'page'|'part'|'range'|'selected'} namingMode
 * @param {number}   [maxDisplay=5]
 * @returns {string[]}
 */
export function previewFileNames(base, groups, namingMode, maxDisplay = 5) {
  if (!groups || groups.length === 0) return [];

  if (groups.length === 1) {
    const suffix = namingMode === 'selected' ? 'selected-pages' : 'split';
    return [`${base}-${suffix}.pdf`];
  }

  const names = groups.slice(0, maxDisplay).map((group, i) =>
    groupFileName(base, i, group, namingMode)
  );

  if (groups.length > maxDisplay) {
    names.push(`+ ${groups.length - maxDisplay} more file${groups.length - maxDisplay > 1 ? 's' : ''}`);
  }

  return names;
}
